import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Update canonical URL
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    const canonicalUrl = canonical ? `${siteUrl}${canonical}` : `${siteUrl}${window.location.pathname}`;
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Update OG URL
    updateMetaTag('og:url', canonicalUrl, true);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, schema]);
}

export function generateWebPageSchema(data: {
  name: string;
  description: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: data.name,
    description: data.description,
    url: `${siteUrl}${data.url}`,
    publisher: {
      '@type': 'Organization',
      name: 'Rooted & Rising',
      url: siteUrl
    }
  };
}

export function generateBlogPostingSchema(data: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: data.headline,
    description: data.description,
    url: `${siteUrl}${data.url}`,
    datePublished: data.datePublished,
    dateModified: data.dateModified || data.datePublished,
    author: {
      '@type': 'Person',
      name: data.author || 'Rooted & Rising'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Rooted & Rising',
      url: siteUrl
    }
  };
}

export function generateProductSchema(data: {
  name: string;
  description: string;
  price: number;
  currency?: string;
  availability?: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.name,
    description: data.description,
    offers: {
      '@type': 'Offer',
      price: data.price,
      priceCurrency: data.currency || 'USD',
      availability: data.availability || 'https://schema.org/InStock',
      url: `${siteUrl}${data.url}`
    }
  };
}

export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Rooted & Rising',
    url: siteUrl,
    description: 'A family operating system for intentional living through micro-habits',
    sameAs: []
  };
}